import api from "@/api/index";

/**
 * Service pour la gestion des animaux dans le calculateur de ration
 */
export const animauxService = {
  /**
   * Récupère la liste des animaux d'un propriétaire
   * @param {number} proprietaireId - ID du propriétaire
   * @returns {Promise} Promesse contenant la liste des animaux
   */
  async getAnimauxByProprietaire(proprietaireId) {
    try {
      const response = await api.get(`/ration/proprietaire/${proprietaireId}/animaux`);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des animaux:", error);
      throw error;
    }
  },

  /**
   * Crée un nouvel animal
   * @param {Object} animalData - Les données de l'animal à créer
   * @param {string} animalData.name - Nom de l'animal
   * @param {string} animalData.species - Espèce de l'animal
   * @param {string} animalData.breed - Race de l'animal
   * @param {string} animalData.birthDate - Date de naissance
   * @param {number} animalData.weight - Poids de l'animal
   * @param {number} animalData.ownerId - ID du propriétaire
   * @param {number} animalData.animalTypeId - ID du type d'animal
   * @param {number} animalData.raceId - ID de la race
   * @returns {Promise} Promesse contenant les données du nouvel animal
   */
  async createAnimal(animalData) {
    try {
      const response = await api.post("/animals", animalData);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la création de l'animal:", error);
      throw error;
    }
  },

  /**
   * Met à jour un animal existant
   * @param {number} animalId - ID de l'animal à modifier
   * @param {Object} animalData - Les données de l'animal à mettre à jour
   * @param {string} animalData.name - Nom de l'animal
   * @param {string} animalData.species - Espèce de l'animal
   * @param {string} animalData.breed - Race de l'animal
   * @param {string} animalData.birthDate - Date de naissance
   * @param {number} animalData.weight - Poids de l'animal
   * @param {number} animalData.ownerId - ID du propriétaire
   * @param {number} animalData.animalTypeId - ID du type d'animal
   * @param {number} animalData.raceId - ID de la race
   * @returns {Promise} Promesse contenant les données de l'animal modifié
   */
  async updateAnimal(animalId, animalData) {
    try {
      const response = await api.put(`/animals/${animalId}`, animalData);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la modification de l'animal:", error);
      throw error;
    }
  },

  /**
   * Récupère la liste des types d'animaux
   * @returns {Promise} Promesse contenant la liste des types d'animaux
   */
  async getAnimalTypes() {
    try {
      const response = await api.get("/animal-types");
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des types d'animaux:", error);
      throw error;
    }
  },

  /**
   * Récupère la liste des races d'animaux
   * @returns {Promise} Promesse contenant la liste des races d'animaux
   */
  async getAnimalRaces() {
    try {
      const response = await api.get("/animal-races");
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des races d'animaux:", error);
      throw error;
    }
  },

  /**
   * Récupère la liste des stades physiologiques des animaux
   * @returns {Promise} Promesse contenant la liste des stades physiologiques
   */
  async getAnimalPhysiologicalStages(animalTypeId = null) {
    try {
      const url = animalTypeId
        ? `/animal-physiological-stages?animalType=${animalTypeId}`
        : "/animal-physiological-stages";
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des stades physiologiques:", error);
      throw error;
    }
  },

  /**
   * Récupère la liste des activités quotidiennes des animaux
   * @returns {Promise} Promesse contenant la liste des activités quotidiennes
   */
  async getAnimalDailyActivities(animalTypeId = null) {
    try {
      const url = animalTypeId
        ? `/animal-daily-activities?animalType=${animalTypeId}`
        : "/animal-daily-activities";
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des activités quotidiennes:", error);
      throw error;
    }
  },

  /**
   * Récupère la liste des options de gestion de surpoids des animaux
   * @param {number} animalTypeId - ID du type d'animal pour filtrer (optionnel)
   * @returns {Promise} Promesse contenant la liste des options de gestion de surpoids
   */
  async getAnimalWeightManagements(animalTypeId = null) {
    try {
      const url = animalTypeId
        ? `/animal-weight-managements?animalType=${animalTypeId}`
        : "/animal-weight-managements";
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des options de gestion de surpoids:", error);
      throw error;
    }
  },
};
